import React, { useState } from 'react';
import Header from '../components/layout/Header';
import SideMenu from '../components/layout/SideMenu';
import Alert from '../components/common/Alert';
import Pagination from '../components/common/Pagination';
import UserFormModal from '../components/products/UserFormModal';
import { useUsers } from '../hooks/useUsers';
import styles from './UsersPage.module.css';
import tableStyles from '../styles/shared/DataTable.module.css';
import layoutStyles from '../styles/layout.module.css';
import placeholderImage from '../assets/placeholder.png';

const UsersPage: React.FC = () => {
  const [searchInput, setSearchInput] = useState('');
  const {
    isAdmin,
    currentUser,
    users,
    roles,
    isLoading,
    isSubmitting,
    isModalOpen,
    editingUser,
    alert,
    hideAlert,
    handleAddUser,
    handleEditUser,
    handleFormSubmit,
    handleDeleteUser,
    closeModal,
    pagination,
  } = useUsers();

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    pagination.setSearch(searchInput.trim());
    pagination.setPage(1);
  };

  if (!isAdmin) {
    return (
      <div className={layoutStyles.pageContainer}>
        <Header />
        <div className={styles.mainLayout}>
          <SideMenu />
          <main className={styles.contentArea}>
            <p className={styles.accessDenied} title="Access denied. Admin privileges required.">
              Access denied. Admin privileges required.
            </p>
          </main>
        </div>
      </div>
    );
  }

  if (isLoading && users.length === 0) {
    return (
      <div className={layoutStyles.pageContainer}>
        <Header />
        <div className={styles.mainLayout}>
          <SideMenu />
          <main className={styles.contentArea}>
            <p className={styles.loadingMessage} title="Loading users...">Loading users...</p>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className={layoutStyles.pageContainer}>
      <Header />
      <div className={styles.mainLayout}>
        <SideMenu />
        <main className={styles.contentArea}>
          <Alert
            message={alert.message}
            type={alert.type}
            isVisible={alert.isVisible}
            onClose={hideAlert}
          />

          <div className={styles.titleBar}>
            <h2 className={styles.title} title="User Management">User Management</h2>
            <button onClick={handleAddUser} className={styles.addButton} title="Add User">
              Add User
            </button>
          </div>

          <div className={styles.toolbar}>
            <form onSubmit={handleSearchSubmit} style={{ display: 'flex', gap: '0.5rem' }}>
              <input
                type="text"
                placeholder="Search users..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className={styles.searchInput}
              />
              <button type="submit" className={styles.searchButton}>Search</button>
            </form>
            <label className={styles.sortLabel}>
              Sort by:
              <select
                value={`${pagination.sortBy}-${pagination.sortOrder}`}
                onChange={(e) => {
                  const [by, order] = e.target.value.split('-') as [string, 'asc' | 'desc'];
                  pagination.setSortBy(by);
                  pagination.setSortOrder(order);
                  pagination.setPage(1);
                }}
                className={styles.sortSelect}
              >
                <option value="id-asc">ID (asc)</option>
                <option value="id-desc">ID (desc)</option>
                <option value="username-asc">Username (A–Z)</option>
                <option value="username-desc">Username (Z–A)</option>
                <option value="email-asc">Email (A–Z)</option>
                <option value="email-desc">Email (Z–A)</option>
                <option value="first_name-asc">First name (A–Z)</option>
                <option value="first_name-desc">First name (Z–A)</option>
              </select>
            </label>
          </div>

          {users.length === 0 && !isLoading ? (
            <div className={styles.emptyState}>
              <p title="No users found. Click Add User to create one.">
                No users found. Click &quot;Add User&quot; to create one.
              </p>
              <button onClick={handleAddUser} className={styles.addButton} title="Add User">
                Add User
              </button>
            </div>
          ) : (
            <div className={styles.tableContainer}>
              <table className={tableStyles.dataTable}>
                <thead>
                  <tr>
                    <th title="Image">Image</th>
                    <th title="Username">Username</th>
                    <th title="Name">Name</th>
                    <th title="Email">Email</th>
                    <th title="Role">Role</th>
                    <th title="Mobile">Mobile</th>
                    <th title="Actions">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id}>
                      <td className={tableStyles.imageCell} title={user.username}>
                        {user.image_url ? (
                          <img
                            src={user.image_url}
                            alt={user.username}
                            title={user.username}
                            className={tableStyles.tableImage}
                            onError={(e) => { e.currentTarget.src = placeholderImage; }}
                          />
                        ) : (
                          <div className={tableStyles.tablePlaceholder} title={user.username}>👤</div>
                        )}
                      </td>
                      <td title={user.username}>{user.username}</td>
                      <td title={`${user.first_name} ${user.last_name}`}>
                        {user.first_name} {user.last_name}
                      </td>
                      <td title={user.email}>{user.email}</td>
                      <td title={user.role?.role_name || 'No role'}>
                        {user.role?.role_name || 'N/A'}
                      </td>
                      <td title={user.mobile_number || 'N/A'}>
                        {user.mobile_number || 'N/A'}
                      </td>
                      <td>
                        <div className={styles.actionButtons}>
                          <button
                            onClick={() => handleEditUser(user)}
                            className={`${styles.actionButton} ${styles.editButton}`}
                            title="Edit User"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteUser(user.id)}
                            className={`${styles.actionButton} ${styles.deleteButton}`}
                            title="Delete User"
                            disabled={user.id === currentUser?.id}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {pagination.total > 0 && (
            <Pagination
              page={pagination.page}
              totalPages={pagination.totalPages}
              total={pagination.total}
              limit={pagination.limit}
              onPageChange={pagination.setPage}
              onLimitChange={(newLimit) => { pagination.setLimit(newLimit); pagination.setPage(1); }}
              limitOptions={[10, 20, 50]}
              showLimitSelector
              disabled={isLoading}
            />
          )}

          <UserFormModal
            isOpen={isModalOpen}
            onClose={closeModal}
            onSubmit={handleFormSubmit}
            initialData={editingUser}
            roles={roles}
            isLoading={isSubmitting}
          />
        </main>
      </div>
    </div>
  );
};

export default UsersPage;
